import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { DollarSign, Sparkles, HeartHandshake } from 'lucide-react';
import { TranslatedText } from '@/components/TranslatedText';

const painPoints = [
  {
    icon: DollarSign,
    title: "Over $1M Generated by Our Community",
    description: "In the last 3 months alone, members of our academy have generated more than $1,000,000 in online revenue by applying the exact strategies we teach.",
  },
  {
    icon: Sparkles,
    title: "Constantly Improving",
    description: "We continuously upgrade the academy experience. Our team has introduced personalized lessons with interactive video classes designed to stay engaging and never boring — making the academy better every day.",
  },
  {
    icon: HeartHandshake,
    title: "You Can Quit Anytime — But We Won't Quit On You",
    description: "We provide the highest level of support possible, connecting you with people already achieving the results you want. You'll learn from their experience, avoid common mistakes, and accelerate your start with a real advantage. Commit to the process — success becomes inevitable.",
  },
];

export const LightProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} id="problem" className="py-12 sm:py-20 px-4 bg-background">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="text-sm font-medium text-muted-foreground tracking-widest uppercase mb-4 block">
            <TranslatedText>Why us?</TranslatedText>
          </span>
          
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3 px-2">
            <TranslatedText>Built for Real Results</TranslatedText>
          </h2>
          <p className="text-base text-muted-foreground max-w-lg mx-auto">
            <TranslatedText>Here's what makes our community different.</TranslatedText>
          </p>
        </motion.div>

        {/* Cards */}
        <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-5">
          {painPoints.map((point, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="p-5 rounded-2xl bg-card border border-border hover:border-primary/20 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center mb-4">
                <point.icon className="w-5 h-5 text-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                <TranslatedText>{point.title}</TranslatedText>
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                <TranslatedText>{point.description}</TranslatedText>
              </p>
            </motion.div>
          ))}
        </div>

        {/* Transition */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center mt-10 pt-10 border-t border-border"
        >
          <p className="text-lg sm:text-xl text-muted-foreground mb-2">
            <TranslatedText>What if there was a proven system to build income...</TranslatedText>
          </p>
          <p className="text-xl sm:text-2xl font-bold text-primary">
            <TranslatedText>Without ever showing your face?</TranslatedText>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default LightProblemSection;
